import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import type { ICodeType } from '@/services/iCodeService';

export interface ICodeSession {
  type: 'icode';
  iCodeType: ICodeType;
  patientKey: number;
  orderKey?: number | null;
  studyKey?: number | null;
  /** True when the matched study had already progressed past scheduling at verify time.
   *  Only meaningful for iCodeType 'GFE' — selects between the /exams-only and
   *  /appointments-only capability set in useSessionCaps. */
  isExamReported?: boolean;
  /** True when this patient already has a c_user account. Drives the "log in to your
   *  account" shortcut AppLayout shows in place of the restricted iCode session. */
  hasAccount?: boolean;
}

export interface GuestSession {
  type: 'guest';
  patientKey: number;
  /** Every study key booked in the session this guest was exited from — used to scope the
   *  post-booking /appointments view to just those appointments. */
  bookedStudyKeys?: number[];
  /** True when this guest may be offered account creation. For new guest-scheduling patients this
   *  also means they passed the account-creation age gate. Drives the "Create an account" nav card
   *  shown in place of the sign-in form on the guest appointments view. */
  canCreateAccount?: boolean;
  /** True when this guest was created by exiting an iCode order/reschedule session whose patient
   *  already has a portal account. Drives the nav sign-in form so they can log in for full access. */
  hasAccount?: boolean;
}

// Persisted to localStorage: safe metadata for UI hydration before session ping completes.
// Contains everything needed to render the authenticated shell (name, nav, route guards)
// but never the JWT token itself.
export interface AuthUserMeta {
  id: string;
  fullName: string;
  roles: string[];
  session?: ICodeSession | GuestSession;
  /**
   * True when c_user.force_password_reset_flag = 'Y' at login.
   * Persisted to localStorage (via atomWithStorage) so the constraint survives tab close/reopen
   * for the duration of the session — we only read the flag from the server once, at login.
   * Cleared to false by Account.tsx onSuccess after a successful password change, which also
   * clears the flag on the backend (force_password_reset_flag = 'N').
   * PasswordResetGuard reads this to intercept all protected-route navigation until reset is done.
   */
  passwordMustBeReset?: boolean;
}

// Persisted metadata — no token field.
export const authUserMetaAtom = atomWithStorage<AuthUserMeta | null>('auth_user_meta', null);

// Persisted to localStorage alongside authUserMetaAtom so the CSRF token survives page
// refreshes and new tabs. This eliminates the race condition where the user could submit
// the login form before bootstrapAuth()'s async GET /SessionStatus returned the token.
// localStorage is safe here: cross-origin JavaScript cannot read it, which is the same
// property the double-submit CSRF pattern relies on. Cleared in clearAuthAndRedirect().
export const csrfTokenAtom = atomWithStorage<string | null>('csrf_token', null);

// True while the bootstrap SessionStatus ping is in-flight.
// ProtectedRoute renders a spinner instead of redirecting during this window.
export const isRehydratingAtom = atom(false);

// Authenticated when persisted meta is present (BFF session cookie holds the JWT server-side).
export const isAuthenticatedAtom = atom(
  (get) => get(authUserMetaAtom) !== null,
);

/** Set while viewing another patient's records as an authorized delegate. */
export interface ViewingAsPatient {
  patientKey: number;
  name: string;
}

export const viewingAsAtom = atomWithStorage<ViewingAsPatient | null>('viewing_as', null);

/**
 * The delegate's own metadata, stashed while `authUserMetaAtom` temporarily reflects the
 * patient-context being viewed (see MedicalRecordAccessController.ActivatePatientContext).
 * "Switch back" restores this into authUserMetaAtom rather than requiring a re-login.
 */
export const homeUserMetaAtom = atomWithStorage<AuthUserMeta | null>('home_user_meta', null);

/**
 * The one deliberate exception to "cookie/session based, not client-JWT based" (see
 * PatientPortal.Bff's CLAUDE.md, "Delegate-Context Tokens"): ActivatePatientContext mints a
 * scoped JWT that the frontend must attach directly as a client-held Authorization: Bearer
 * header on subsequent requests — the BFF only honors it when a live session for the same
 * browser still exists and the token's actingUserId claim matches that session's own identity.
 */
export const delegateTokenAtom = atomWithStorage<string | null>('delegate_token', null);

/**
 * Write-only action atom: clears all three delegate-viewing atoms in one call. All three
 * are atomWithStorage (localStorage), so they survive tab close/reopen — anything that
 * establishes a genuinely NEW top-level session (a fresh login, accept-invite, guest/iCode
 * flow) must call this before/alongside setting authUserMetaAtom, or a leftover "viewing as"
 * state from an earlier session that ended without an explicit logout or switch-back (closed
 * tab, expired token, logging in directly as a different user) will silently survive into
 * the new session — the banner then shows a stale patient name that has nothing to do with
 * whichever patient's data is actually being displayed. Only switchTo/switchBack (which set
 * or clear these atoms as a matched, intentional pair) should NOT call this.
 */
export const clearDelegateViewingAtom = atom(null, (_get, set) => {
  set(viewingAsAtom, null);
  set(homeUserMetaAtom, null);
  set(delegateTokenAtom, null);
});

/**
 * Identity to use for Account/Patient-scope data (profile, password, security questions,
 * health restrictions, MFA, authorized-access management) — matches Gen2: those pages always
 * reflect the logged-in user's own identity, never the actively-viewed delegate patient.
 * While viewing-as is active, authUserMetaAtom temporarily holds the delegate patient's own
 * context (see homeUserMetaAtom's doc comment), so this atom falls back to homeUserMetaAtom
 * whenever it's set; outside a delegate session it's just authUserMetaAtom.
 */
export const accountIdentityAtom = atom((get) => get(homeUserMetaAtom) ?? get(authUserMetaAtom));
